import React from "react";
import { useRouter } from "next/router";
import { BuilderComponent, builder, useIsPreviewing } from "@builder.io/react";
import DefaultErrorPage from "next/error";
import Head from "next/head";
import { BuilderContent } from "@builder.io/sdk";
import { GetStaticProps } from "next";
import GoogleTagManager from "@/components/GoogleTagManager";
import '@builder.io/widgets';
import IntercomChat from "@/components/IntercomChat/IntercomChat";


builder.init(process.env.NEXT_PUBLIC_BUILDER_API_KEY!);

// Define a function that fetches the Builder
// content for a given page
export const getStaticProps: GetStaticProps = async ({ params }) => {
  // Fetch the builder content for the given page
  const page = await builder
    .get("page", {
      userAttributes: {
        urlPath: "/" + ((params?.page as string[])?.join("/") || ""),
      },
    })
    .toPromise();

  // If the page is not found, return notFound: true to render the 404 page
  if (!page) {
    return {
      notFound: true,
      // Optionally, set a revalidation period for the 404 status itself
      revalidate: 60, // e.g., revalidate every 60 seconds
    };
  }

  // Return the page content as props
  return {
    props: {
      page: page, // page will be BuilderContent here, not null
    },
    // Revalidate the content every 5 seconds
    revalidate: 5,
  };
};
// Define a function that generates the
// static paths for all pages in Builder
export async function getStaticPaths() {
  // Get a list of all pages in Builder
  const pages = await builder.getAll("page", {
    // We only need the URL field
    fields: "data.url",
    options: { noTargeting: true },
  });

  // Generate the static paths for all pages in Builder
  return {
    paths: pages
      .map((page) => String(page.data?.url))
      .filter((url) => url !== "/" && url !== "/404"), // Excluir también /404
    fallback: "blocking",
  };
}

// Define the Page component
// With the change in getStaticProps, 'page' will always be BuilderContent if the component is rendered
// for a found page. For 404s, Next.js will render pages/404.tsx instead.
// However, 'page' can still be null in preview mode if the page doesn't exist yet.
// So, we keep BuilderContent | null for the prop type.
export default function Page({ page }: { page: BuilderContent | null }) {
  const router = useRouter();
  const isPreviewing = useIsPreviewing();

  // If the page content is not available
  // and not in preview mode, show a 404 error page
  if (!page && !isPreviewing) {
    return <DefaultErrorPage statusCode={404} />;
  }

  // If the page content is available, render
  // the BuilderComponent with the page content
  return (
    <div suppressHydrationWarning={true}>
      <Head>
        <title>{page?.data?.title}</title>
        <meta name="google-site-verification" content="CnYUIFjzWweogHQpJRF2qlW4P-s519JHQL-b7kuVuBI" />
        <meta name="description" content={page?.data?.description} />
      </Head>
      {/* Render the Builder page */}
      <BuilderComponent model="page" content={page || undefined} options={{ includeRefs: true }} />
      <IntercomChat />
      <GoogleTagManager gtmId="GTM-MCVBGWP" />
    </div>
  );
}
